import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { POPOVER_BACKDROP, PopoverFooter, popoverBtnStyle } from "./PdfForm";

export function DateTimeInputPopover({
  mode,
  timestamp,
  dateStr,
  label,
  onCommit,
  onCommitDate,
  onClose,
  colors,
  t,
  minTimestamp,
}: {
  mode: "datetime" | "date" | "time";
  timestamp?: number | null;
  dateStr?: string | null;
  label?: string;
  onCommit?: (ts: number | null) => void;
  onCommitDate?: (date: string | null) => void;
  onClose: () => void;
  colors: Record<string, string>;
  t?: (key: string) => string;
  minTimestamp?: number | null;
}) {
  const showDate = mode !== "time";
  const showTime = mode !== "date";

  const base = (() => {
    if (showTime && timestamp) return new Date(timestamp);
    if (!showTime && dateStr) return new Date(dateStr + "T00:00:00");
    return null;
  })();

  const [focused, setFocused] = useState<"day"|"mon"|"year"|"hour"|"min"|null>(null);
  const [dayStr, setDayStr] = useState(base ? String(base.getDate()).padStart(2, "0") : "");
  const [monStr, setMonStr] = useState(base ? String(base.getMonth() + 1).padStart(2, "0") : "");
  const [yearStr, setYearStr] = useState(base ? String(base.getFullYear()) : "");
  const [hourStr, setHourStr] = useState(base ? String(base.getHours()).padStart(2, "0") : "");
  const [minStr, setMinStr] = useState(base ? String(base.getMinutes()).padStart(2, "0") : "");

  const dayRef = useRef<HTMLInputElement>(null);
  const monRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const hourRef = useRef<HTMLInputElement>(null);
  const minRef = useRef<HTMLInputElement>(null);

  const firstRef = showDate ? dayRef : hourRef;

  useEffect(() => {
    const timer = setTimeout(() => {
      firstRef.current?.focus();
      firstRef.current?.select();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const advance = (ref: React.RefObject<HTMLInputElement | null>) => {
    ref.current?.focus();
  };

  // Returns change handler: strips non-digits, caps at maxLen, auto-advances when full
  // or when first digit exceeds autoAdvanceFirst (0 = disabled)
  const mkChange = (
    set: (v: string) => void,
    maxLen: number,
    autoAdvanceFirst: number,
    next: React.RefObject<HTMLInputElement | null> | null,
  ) => (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, maxLen);
    const singleUnambiguous = autoAdvanceFirst > 0 && digits.length === 1 && parseInt(digits) > autoAdvanceFirst;
    // Pad to full width when auto-advancing on a single digit so "3" shows as "03"
    set(singleUnambiguous ? digits.padStart(maxLen, "0") : digits);
    if (next && (digits.length === maxLen || singleUnambiguous)) {
      advance(next);
    }
  };

  const mkKeyDown = (
    next: React.RefObject<HTMLInputElement | null> | null,
    prev: React.RefObject<HTMLInputElement | null> | null,
    seps: string[],
  ) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (next && (seps.includes(e.key) || e.key === "ArrowRight")) {
      e.preventDefault();
      advance(next);
    } else if (prev && (e.key === "Backspace" || e.key === "ArrowLeft") && e.currentTarget.value === "") {
      e.preventDefault();
      advance(prev);
    } else if (e.key === "Enter") {
      commit();
    }
  };

  const setNow = () => {
    const now = new Date();
    if (showDate) {
      setDayStr(String(now.getDate()).padStart(2, "0"));
      setMonStr(String(now.getMonth() + 1).padStart(2, "0"));
      setYearStr(String(now.getFullYear()));
    }
    if (showTime) {
      setHourStr(String(now.getHours()).padStart(2, "0"));
      setMinStr(String(now.getMinutes()).padStart(2, "0"));
    }
    firstRef.current?.focus();
    firstRef.current?.select();
  };

  const commit = () => {
    if (mode === "date") {
      const d = Math.min(Math.max(parseInt(dayStr || "1"), 1), 31);
      const m = Math.min(Math.max(parseInt(monStr || "1"), 1), 12);
      const y = parseInt(yearStr || String(new Date().getFullYear()));
      onCommitDate?.(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    } else {
      const h = Math.min(Math.max(parseInt(hourStr || "0"), 0), 23);
      const m = Math.min(Math.max(parseInt(minStr || "0"), 0), 59);
      const d = mode === "time"
        ? (base ? new Date(timestamp!) : new Date())
        : (() => {
            const day = Math.min(Math.max(parseInt(dayStr || "1"), 1), 31);
            const mon = Math.min(Math.max(parseInt(monStr || "1"), 1), 12);
            const yr = parseInt(yearStr || String(new Date().getFullYear()));
            return new Date(yr, mon - 1, day);
          })();
      d.setHours(h, m, 0, 0);
      const ts = minTimestamp ? Math.max(d.getTime(), minTimestamp) : d.getTime();
      onCommit?.(ts);
    }
    onClose();
  };

  const segStyle = (w: string, field: typeof focused): React.CSSProperties => ({
    width: w,
    textAlign: "center",
    border: "none",
    borderBottom: `2px solid ${colors.formInk}`,
    borderRadius: "0.15em",
    outline: "none",
    background: focused === field ? colors.formInk + "33" : "transparent",
    fontSize: "inherit",
    fontFamily: "inherit",
    fontWeight: 700,
    color: colors.formInk,
    caretColor: "transparent",
    padding: "0 0.15em",
    boxSizing: "content-box",
  });

  const Sep = ({ char }: { char: string }) => (
    <span style={{ fontWeight: 700, color: colors.formInk, userSelect: "none" }}>{char}</span>
  );

  const nextAfterDate = showTime ? hourRef : null;

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => { if (e.target === e.currentTarget) commit(); }}
    >
      <div style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "0.5em",
        boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
        maxWidth: "min(95vw, 32em)",
        fontSize: "1rem",
        lineHeight: "normal",
        overflow: "hidden",
      }}>
        {label && (
          <div style={{ fontSize: "0.8em", fontWeight: 600, color: colors.textMuted, padding: "0.5em 0.75em 0", textAlign: "center" }}>
            {label}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.4em", padding: "1.2em 1em", fontSize: "2.2em" }}>
          {showDate && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.15em" }}>
              <input
                ref={dayRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t?.("form.day") ?? "DD"}
                value={dayStr}
                onChange={(e) => mkChange(setDayStr, 2, 3, monRef)(e.target.value)}
                onKeyDown={mkKeyDown(monRef, null, ["."])}
                onFocus={(e) => { setFocused("day"); const el = e.target; setTimeout(() => el.select(), 0); }}
                onBlur={() => setFocused(null)}
                maxLength={2}
                style={segStyle("3ch", "day")}
              />
              <Sep char="." />
              <input
                ref={monRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t?.("form.month") ?? "MM"}
                value={monStr}
                onChange={(e) => mkChange(setMonStr, 2, 1, yearRef)(e.target.value)}
                onKeyDown={mkKeyDown(yearRef, dayRef, ["."])}
                onFocus={(e) => { setFocused("mon"); const el = e.target; setTimeout(() => el.select(), 0); }}
                onBlur={() => setFocused(null)}
                maxLength={2}
                style={segStyle("3ch", "mon")}
              />
              <Sep char="." />
              <input
                ref={yearRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t?.("form.year") ?? "YYYY"}
                value={yearStr}
                onChange={(e) => mkChange(setYearStr, 4, 0, nextAfterDate)(e.target.value)}
                onKeyDown={mkKeyDown(nextAfterDate, monRef, ["."])}
                onFocus={(e) => { setFocused("year"); const el = e.target; setTimeout(() => el.select(), 0); }}
                onBlur={() => setFocused(null)}
                maxLength={4}
                style={segStyle("5ch", "year")}
              />
            </div>
          )}
          {showTime && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.15em" }}>
              <input
                ref={hourRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t?.("form.hour") ?? "HH"}
                value={hourStr}
                onChange={(e) => mkChange(setHourStr, 2, 2, minRef)(e.target.value)}
                onKeyDown={mkKeyDown(minRef, showDate ? yearRef : null, [":"])}
                onFocus={(e) => { setFocused("hour"); const el = e.target; setTimeout(() => el.select(), 0); }}
                onBlur={() => setFocused(null)}
                maxLength={2}
                style={segStyle("3ch", "hour")}
              />
              <Sep char=":" />
              <input
                ref={minRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t?.("form.minute") ?? "MM"}
                value={minStr}
                onChange={(e) => mkChange(setMinStr, 2, 0, null)(e.target.value)}
                onKeyDown={mkKeyDown(null, hourRef, [])}
                onFocus={(e) => { setFocused("min"); const el = e.target; setTimeout(() => el.select(), 0); }}
                onBlur={() => setFocused(null)}
                maxLength={2}
                style={segStyle("3ch", "min")}
              />
            </div>
          )}
        </div>
        <PopoverFooter
          onClear={() => {
            if (mode === "date") onCommitDate?.(null);
            else onCommit?.(null);
            onClose();
          }}
          onCommit={commit}
          colors={colors}
        >
          <button onClick={setNow} style={{ ...popoverBtnStyle(colors), color: colors.text }}>
            {t?.("form.now") ?? "Зараз"}
          </button>
        </PopoverFooter>
      </div>
    </div>,
    document.body,
  );
}
